/**
 * RCAForm — creare / editare RCA (PRD_RCA_v1 + clarificări: rootCauses la createRCA).
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { rcaService } from '../../services/rcaService';
import { deviationService } from '../../services/deviationService';
import { incidentService } from '../../services/incidentService';
import Modal from '../ui/Modal';
import './RCAForm.css';

const CAUSE_TYPES = [
  { value: 'human', labelKey: 'causeTypeHuman' },
  { value: 'process', labelKey: 'causeTypeProcess' },
  { value: 'equipment', labelKey: 'causeTypeEquipment' },
  { value: 'environment', labelKey: 'causeTypeEnvironment' },
  { value: 'management', labelKey: 'causeTypeManagement' }
];

const STATUS_OPTIONS = [
  { value: 'open', labelKey: 'statusOpen' },
  { value: 'under_investigation', labelKey: 'statusUnderInvestigation' },
  { value: 'resolved', labelKey: 'statusResolved' }
];

const emptyForm = () => ({
  deviationId: '',
  incidentId: '',
  status: 'open',
  assignedTo: '',
  assignedToName: '',
  rootCauses: [],
  contributingFactorsText: '',
  findings: '',
  recommendations: '',
  protocolId: '',
  protocolName: '',
  riskId: '',
  riskName: ''
});

const RCAForm = ({
  organizationId,
  userId,
  userRole,
  rcaId = null,
  deviationId: deviationIdProp = null,
  incidentId: incidentIdProp = null,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation('rca');
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [capaIds, setCapaIds] = useState([]);
  const [lockDeviation, setLockDeviation] = useState(!!deviationIdProp);
  const [lockIncident, setLockIncident] = useState(!!incidentIdProp);

  const canEdit = ['management', 'ic_personnel'].includes(userRole);
  const isEdit = !!rcaId;

  useEffect(() => {
    let cancelled = false;

    async function loadContext() {
      if (rcaId) {
        setLoading(true);
        try {
          const rca = await rcaService.getRCA(rcaId);
          if (!cancelled && rca) {
            setForm({
              deviationId: rca.deviationId || '',
              incidentId: rca.incidentId || '',
              status: rca.status || 'open',
              assignedTo: rca.assignedTo || '',
              assignedToName: rca.assignedToName || '',
              rootCauses: rca.rootCauses || [],
              contributingFactorsText: rca.contributingFactorsText || '',
              findings: rca.findings || '',
              recommendations: rca.recommendations || '',
              protocolId: rca.protocolId || '',
              protocolName: rca.protocolName || '',
              riskId: rca.riskId || '',
              riskName: rca.riskName || ''
            });
            setCapaIds(rca.capaIds || []);
            setLockDeviation(true);
            setLockIncident(!!rca.incidentId);
          }
        } catch (e) {
          if (!cancelled) setError(e.message);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        let devId = deviationIdProp || '';
        const next = emptyForm();

        if (incidentIdProp) {
          const inc = await incidentService.getIncident(incidentIdProp);
          if (!cancelled && inc) {
            if (inc.deviationId) devId = inc.deviationId;
            next.incidentId = incidentIdProp;
            next.deviationId = devId;
            next.protocolId = inc.protocolId || '';
            next.riskId = inc.riskId || '';
            setLockIncident(true);
          }
        }

        if (deviationIdProp) devId = deviationIdProp;

        if (devId) {
          const dev = await deviationService.getDeviation(devId);
          if (!cancelled && dev) {
            next.deviationId = devId;
            next.protocolId = dev.protocolId || next.protocolId;
            next.protocolName = dev.protocolName || '';
            next.riskId = dev.riskId || next.riskId;
            next.riskName = dev.riskName || '';
            setLockDeviation(true);
          }
        } else if (deviationIdProp) {
          next.deviationId = deviationIdProp;
          setLockDeviation(true);
        }

        if (!cancelled) setForm(next);
      } catch (e) {
        if (!cancelled) console.warn('[RCAForm] load context:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadContext();
    return () => {
      cancelled = true;
    };
  }, [rcaId, deviationIdProp, incidentIdProp]);

  const addCause = () => {
    const newCause = {
      causeId: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}`,
      causeType: 'human',
      description: '',
      isContributingFactor: false
    };
    setForm((prev) => ({ ...prev, rootCauses: [...prev.rootCauses, newCause] }));
  };

  const updateCause = (causeId, field, value) => {
    setForm((prev) => ({
      ...prev,
      rootCauses: prev.rootCauses.map((c) => (c.causeId === causeId ? { ...c, [field]: value } : c))
    }));
  };

  const removeCause = (causeId) => {
    setForm((prev) => ({
      ...prev,
      rootCauses: prev.rootCauses.filter((c) => c.causeId !== causeId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    if (!form.deviationId?.trim()) {
      setError(t('errorNoDeviation'));
      return;
    }
    if (form.rootCauses.length === 0) {
      setError(t('errorNoCauses'));
      return;
    }
    for (const c of form.rootCauses) {
      if (!c.description?.trim()) {
        setError(t('errorNoCauses'));
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await rcaService.updateRCA(
          rcaId,
          {
            status: form.status,
            assignedTo: form.assignedTo || null,
            assignedToName: form.assignedToName || '',
            rootCauses: form.rootCauses,
            contributingFactorsText: form.contributingFactorsText,
            findings: form.findings,
            recommendations: form.recommendations
          },
          userId
        );
      } else {
        await rcaService.createRCA(
          {
            organizationId,
            deviationId: form.deviationId.trim(),
            incidentId: form.incidentId?.trim() || null,
            rootCauses: form.rootCauses,
            contributingFactorsText: form.contributingFactorsText,
            findings: form.findings,
            recommendations: form.recommendations,
            assignedTo: form.assignedTo || null,
            assignedToName: form.assignedToName || '',
            protocolId: form.protocolId || null,
            protocolName: form.protocolName || null,
            riskId: form.riskId || null,
            riskName: form.riskName || null
          },
          userId
        );
      }
      onSave?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseRCA = async () => {
    if (!canEdit || !rcaId) return;
    setSaving(true);
    try {
      await rcaService.closeRCA(rcaId, userId);
      setShowCloseConfirm(false);
      onSave?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rca-form-overlay">
        <div className="rca-form-container">
          <div className="rca-form-loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rca-form-overlay">
      <div className="rca-form-container">
        <div className="rca-form-header">
          <h3>{isEdit ? t('editTitle') : t('createTitle')}</h3>
          <button type="button" className="btn-icon" onClick={onCancel}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="rca-form">
          <div className="rca-form-section">
            <h4>{t('sectionSource')}</h4>
            <div className="form-row">
              <label>{t('deviationId')} *</label>
              <input
                type="text"
                value={form.deviationId}
                onChange={(e) => setForm((prev) => ({ ...prev, deviationId: e.target.value }))}
                disabled={!canEdit || lockDeviation}
                required
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>{t('incidentId')}</label>
              <input
                type="text"
                value={form.incidentId}
                onChange={(e) => setForm((prev) => ({ ...prev, incidentId: e.target.value }))}
                disabled={!canEdit || lockIncident}
                className="form-input"
              />
            </div>
            {form.riskName && (
              <div className="form-row">
                <label>{t('risk')}</label>
                <span className="form-static">{form.riskName}</span>
              </div>
            )}
            {form.protocolName && (
              <div className="form-row">
                <label>{t('protocol')}</label>
                <span className="form-static">{form.protocolName}</span>
              </div>
            )}
          </div>

          <div className="rca-form-section">
            <h4>{t('sectionInvestigation')}</h4>
            <div className="form-row two-cols">
              <div>
                <label>{t('status')}</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                  disabled={!canEdit || form.status === 'closed'}
                  className="form-input"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>{t('assignedTo')}</label>
                <input
                  type="text"
                  value={form.assignedToName}
                  onChange={(e) => setForm((prev) => ({ ...prev, assignedToName: e.target.value }))}
                  disabled={!canEdit}
                  placeholder={t('assignedToPlaceholder')}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          <div className="rca-form-section">
            <div className="section-header-row">
              <h4>{t('sectionCauses')}</h4>
              {canEdit && (
                <button type="button" className="btn-secondary btn-sm" onClick={addCause}>
                  <Plus size={14} /> {t('addCause')}
                </button>
              )}
            </div>
            {form.rootCauses.length === 0 && <p className="rca-empty-causes">{t('noCauses')}</p>}
            {form.rootCauses.map((cause, idx) => (
              <div key={cause.causeId} className="rca-cause-row">
                <div className="cause-row-header">
                  <span className="cause-number">
                    {t('cause')} #{idx + 1}
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn-icon danger"
                      onClick={() => removeCause(cause.causeId)}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="cause-row-fields two-cols">
                  <div>
                    <label>{t('causeType')}</label>
                    <select
                      value={cause.causeType}
                      onChange={(e) => updateCause(cause.causeId, 'causeType', e.target.value)}
                      disabled={!canEdit}
                      className="form-input"
                    >
                      {CAUSE_TYPES.map((ct) => (
                        <option key={ct.value} value={ct.value}>
                          {t(ct.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="cause-type-radio">
                    <label>
                      <input
                        type="radio"
                        checked={!cause.isContributingFactor}
                        onChange={() => updateCause(cause.causeId, 'isContributingFactor', false)}
                        disabled={!canEdit}
                      />
                      {t('causePrincipal')}
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={cause.isContributingFactor}
                        onChange={() => updateCause(cause.causeId, 'isContributingFactor', true)}
                        disabled={!canEdit}
                      />
                      {t('causeContributing')}
                    </label>
                  </div>
                </div>
                <div>
                  <label>{t('causeDescription')} *</label>
                  <textarea
                    value={cause.description}
                    onChange={(e) => updateCause(cause.causeId, 'description', e.target.value)}
                    disabled={!canEdit}
                    rows={2}
                    className="form-input"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="rca-form-section">
            <h4>{t('sectionNarrative')}</h4>
            <div className="form-row">
              <label>{t('contributingFactorsText')}</label>
              <textarea
                value={form.contributingFactorsText}
                onChange={(e) => setForm((prev) => ({ ...prev, contributingFactorsText: e.target.value }))}
                disabled={!canEdit}
                rows={3}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>{t('findings')}</label>
              <textarea
                value={form.findings}
                onChange={(e) => setForm((prev) => ({ ...prev, findings: e.target.value }))}
                disabled={!canEdit}
                rows={3}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>{t('recommendations')}</label>
              <textarea
                value={form.recommendations}
                onChange={(e) => setForm((prev) => ({ ...prev, recommendations: e.target.value }))}
                disabled={!canEdit}
                rows={3}
                className="form-input"
              />
            </div>
          </div>

          {isEdit && capaIds.length > 0 && (
            <div className="rca-form-section">
              <h4>{t('sectionCapa')}</h4>
              <p className="form-static">{capaIds.join(', ')}</p>
            </div>
          )}

          {error && <div className="rca-form-error">{error}</div>}

          <div className="rca-form-footer">
            <div className="footer-left">
              {isEdit && form.status !== 'closed' && canEdit && (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => setShowCloseConfirm(true)}
                  disabled={saving}
                >
                  {t('closeRCA')}
                </button>
              )}
            </div>
            <div className="footer-right">
              <button type="button" onClick={onCancel} disabled={saving}>
                {t('cancel')}
              </button>
              {canEdit && (
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? t('saving') : t('save')}
                </button>
              )}
            </div>
          </div>
        </form>

        <Modal
          isOpen={showCloseConfirm}
          onClose={() => setShowCloseConfirm(false)}
          title={t('closeRCA')}
        >
          <div className="rca-close-confirm">
            <p>{t('closeConfirmMessage')}</p>
            <div className="modal-actions">
              <button type="button" className="btn-danger" onClick={handleCloseRCA} disabled={saving}>
                {t('closeConfirm')}
              </button>
              <button type="button" onClick={() => setShowCloseConfirm(false)} disabled={saving}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default RCAForm;
